/**
 * Render-error reporting for React error boundaries.
 *
 * A boundary that only swallows turns a white screen into a beige one: the
 * user still can't work and nobody can debug it. Every catch goes through
 * here so a broken panel becomes an event with a component stack and — the
 * part that actually shortens the investigation — the id of the stored
 * record that was being rendered when React threw.
 *
 * Three sinks, cheapest first:
 *   1. console  — always, grouped, with the component stack.
 *   2. window   — a `bex:render-error` CustomEvent, so any future telemetry
 *                 can subscribe without this file learning about it.
 *   3. server   — best-effort POST to the existing activity log.
 *
 * Deliberately defensive: reporting runs while the app is already broken, so
 * nothing in here may throw, block, or notify the user.
 */

// A boundary whose fallback also fails can re-enter render in a tight loop.
// Dedupe by fingerprint and hard-cap per page load so a loop costs one report.
const MAX_REPORTS_PER_PAGE_LOAD = 25;
const MAX_COMPONENT_STACK_CHARS = 4000;

const ACTIVITY_PATH = '/api/db/activity/create';
export const RENDER_ERROR_EVENT = 'bex:render-error';

const seen = new Set();
let reportCount = 0;

/**
 * Stable identity for one crash: same boundary + same record + same message
 * + same first stack frame is the same bug, however many times it re-renders.
 */
function fingerprintOf(payload) {
    const firstFrame = String(payload.stack || '').split('\n')[1] || '';
    return [
        payload.boundary,
        payload.recordType,
        payload.recordId,
        payload.message,
        firstFrame.trim()
    ].join('|');
}

function truncate(value, max) {
    const text = String(value || '');
    return text.length > max ? `${text.slice(0, max)}\n…truncated` : text;
}

function describeRecord(payload) {
    if (!payload.recordId && !payload.recordLabel) return '';
    const label = payload.recordLabel ? ` "${payload.recordLabel}"` : '';
    const type = payload.recordType || 'record';
    return ` [${type}${label}${payload.recordId ? ` id=${payload.recordId}` : ''}]`;
}

function logToConsole(payload) {
    const title = `[render-error] ${payload.boundary}${describeRecord(payload)}: ${payload.message}`;
    /* eslint-disable no-console */
    if (typeof console.groupCollapsed === 'function') {
        console.groupCollapsed(title);
        console.error(payload.error);
        console.error('Component stack:', payload.componentStack);
        console.error('Context:', {
            boundary: payload.boundary,
            recordType: payload.recordType,
            recordId: payload.recordId,
            recordLabel: payload.recordLabel,
            url: payload.url
        });
        console.groupEnd();
    } else {
        console.error(title, payload.error, payload.componentStack);
    }
    /* eslint-enable no-console */
}

function emitDomEvent(payload) {
    if (typeof window === 'undefined' || typeof window.CustomEvent !== 'function') return;
    try {
        window.dispatchEvent(new window.CustomEvent(RENDER_ERROR_EVENT, {
            detail: payload
        }));
    } catch (e) {
        // A listener that throws must not take the fallback down with it.
    }
}

/**
 * Best-effort server report.
 *
 * Uses bare `fetch` rather than `bexHttpClient` on purpose: the shared axios
 * client has a response interceptor that toasts server errors, and a failed
 * crash-report should never raise a second alarm on top of the crash. The
 * `keepalive` flag lets the report survive the reload the user is about to
 * hit from the fallback.
 */
function sendToServer(payload) {
    if (process.env.NODE_ENV === 'test') return;
    if (typeof fetch !== 'function') return;
    try {
        const base = process.env.REACT_APP_BEX_SERVER_URL || '';
        fetch(`${base}${ACTIVITY_PATH}`, {
            method: 'POST',
            credentials: 'include',
            keepalive: true,
            headers: {
                'Content-type': 'application/json'
            },
            body: JSON.stringify({
                originalUrl: payload.url,
                method: 'RENDER',
                query: {
                    boundary: payload.boundary,
                    recordType: payload.recordType,
                    recordId: payload.recordId,
                    recordLabel: payload.recordLabel,
                    componentStack: payload.componentStack
                },
                statusCode: 500,
                responseMessage: `[render-error] ${payload.boundary}: ${payload.message}`
            })
        }).catch(() => {
            // Reporting is best-effort. Silence is correct here.
        });
    } catch (e) {
        // Same: never let the reporter break the fallback.
    }
}

/**
 * Report one caught render error.
 *
 * @param {object} details
 * @param {Error} details.error         what React threw
 * @param {object} details.errorInfo    React's `{ componentStack }`
 * @param {string} details.boundary     which boundary caught it
 * @param {string} [details.recordType] e.g. 'api', 'channel', 'opcode'
 * @param {string} [details.recordId]   the stored record being rendered
 * @param {string} [details.recordLabel] human name for that record
 * @returns {object|null} the payload, or null if deduped / capped
 */
export function reportRenderError(details) {
    const source = details || {};
    const error = source.error;
    const payload = {
        boundary: source.boundary || 'unknown',
        recordType: source.recordType || null,
        recordId: source.recordId == null ? null : String(source.recordId),
        recordLabel: source.recordLabel || null,
        message: (error && error.message) || String(error),
        stack: (error && error.stack) || '',
        componentStack: truncate(
            source.errorInfo && source.errorInfo.componentStack,
            MAX_COMPONENT_STACK_CHARS
        ),
        url: typeof window === 'undefined' ? '' : window.location.href,
        at: new Date().toISOString(),
        error
    };

    const id = fingerprintOf(payload);
    if (seen.has(id) || reportCount >= MAX_REPORTS_PER_PAGE_LOAD) return null;
    seen.add(id);
    reportCount += 1;

    logToConsole(payload);
    emitDomEvent(payload);
    sendToServer(payload);
    return payload;
}

/** Test seam — clears the dedupe cache between cases. */
export function __resetRenderErrorReporting() {
    seen.clear();
    reportCount = 0;
}
